/**
 * Rolling-window event GUID generator.
 *
 * Privacy model: the wire payload never carries an absolute timestamp.
 * Each event gets:
 *
 *   eventGuid:  a per-device, per-window UUID
 *   tsRelMs:    milliseconds since the FIRST event of the window
 *
 * Window-close rules — a new window mints a fresh GUID and resets
 * tsRelMs to 0 when ANY of these fire on a new event:
 *
 *   1. First event ever (no current window).
 *
 *   2. IDLE BREAK — gap from previous event > extensionMs (default
 *      10 s). A long quiet patch always cuts the window.
 *
 *   3. ADAPTIVE CLOSE — total window span has exceeded softCapMs
 *      (5 s) AND the current gap is LARGER than the previous gap.
 *      Below the soft cap the window always grows; above it the
 *      window keeps going only while gaps stay flat or shrink (the
 *      cluster is still tight). The first event whose gap is larger
 *      than the one before it ends the window — the cluster is
 *      breaking up.
 *
 *   4. HARD CEILING — total window span exceeds hardCapMs (60 s).
 *      Bounds adversarial / pathological cases where a malicious
 *      client emits perfectly uniform events to keep a single GUID
 *      indefinitely.
 *
 * Consequence for training data: a real cluster of correlated events
 * (a rough patch of road, a pothole+rebound, a sequence of speed bumps)
 * stays in one window even if it lasts 10–30 s, as long as the events
 * remain densely packed. As soon as gaps lengthen, the window closes.
 *
 * Consequence for privacy: per-window analysis is bounded to ≤60 s of
 * activity on one device. No server-visible link between consecutive
 * windows. Time-of-day, day-of-week, and trip duration are
 * unrecoverable from the data alone.
 *
 * GUIDs are generated client-side (crypto.randomUUID) and not
 * persisted across app launches — closing the app discards any active
 * window. The device clock is consulted ONLY for computing within-
 * window deltas (monotonic differences), never the absolute value.
 */
export interface EventWindowOpts {
  /** Idle gap that always cuts the window. Default 10 s. */
  extensionMs?: number;
  /** Below this total span, window always grows. Default 5 s. */
  softCapMs?: number;
  /** Above this total span, window always closes. Default 60 s. */
  hardCapMs?: number;
}

export class EventWindow {
  private readonly extensionMs: number;
  private readonly softCapMs: number;
  private readonly hardCapMs: number;

  private guid: string | null = null;
  private startedAtAbs = 0;
  private lastEventAtAbs = 0;
  /**
   * Gap (ms) between the previous-previous event and the previous
   * event. -1 means we don't have two prior events yet (so the
   * adaptive close check is skipped at index 0 and index 1).
   */
  private prevGap = -1;

  constructor(opts: EventWindowOpts = {}) {
    this.extensionMs = opts.extensionMs ?? 10_000;
    this.softCapMs = opts.softCapMs ?? 5_000;
    this.hardCapMs = opts.hardCapMs ?? 60_000;
  }

  enrich(absTs: number): { eventGuid: string; tsRelMs: number } {
    const isFirstEver = this.guid === null;
    const currentGap = isFirstEver ? 0 : absTs - this.lastEventAtAbs;
    const total = isFirstEver ? 0 : absTs - this.startedAtAbs;

    const idleBreak = !isFirstEver && currentGap > this.extensionMs;
    const adaptiveClose =
      !isFirstEver &&
      total > this.softCapMs &&
      this.prevGap >= 0 &&
      currentGap > this.prevGap;
    const hardCapHit = !isFirstEver && total > this.hardCapMs;

    const startsNew = isFirstEver || idleBreak || adaptiveClose || hardCapHit;

    if (startsNew) {
      this.guid = crypto.randomUUID();
      this.startedAtAbs = absTs;
      this.prevGap = -1; // reset — first event in the new window
    } else {
      // Record this gap so the NEXT event can compare against it.
      this.prevGap = currentGap;
    }
    this.lastEventAtAbs = absTs;

    return {
      eventGuid: this.guid!,
      tsRelMs: Math.max(0, absTs - this.startedAtAbs),
    };
  }
}
